import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * WHAT THE SHELL'S TOP BAR SAYS, DECIDED BY THE PAGE.
 *
 * The design gives every screen one top bar: a title, a sub-line, one search
 * field scoped to that screen, and exactly ONE primary action (§13.3). Those
 * are properties of the page, not of the layout — but the bar itself belongs to
 * the shell, because the shell is what stays put while pages come and go.
 *
 * So a page declares its chrome and the layout renders it. That is what lets
 * ONE shell serve every route: a ported screen fills this in, an un-ported one
 * says nothing and gets a sensible default, and neither has to draw a rail.
 *
 * The pane is here for the same reason. Law 4 makes it an extension of the
 * page's selection, so the page owns its contents — but it is docked beside the
 * content by the shell, and a page that has nothing to dock simply omits it.
 */

export interface ShellChrome {
  title?: ReactNode;
  sub?: ReactNode;
  /** Placeholder. Null hides the field — not every screen has something to search. */
  search?: string | null;
  onSearch?: () => void;
  /** Live search — see TopBar. A list screen types in place. */
  searchValue?: string;
  onSearchChange?: (next: string) => void;
  /** The single contextual primary (§13.3). */
  primary?: { label: ReactNode; onClick: () => void; disabled?: boolean };
  /** The file header's second, menu-shaped control, stacked under the primary. */
  secondary?: { label: ReactNode; onClick: () => void };
  /**
   * A SECOND 200px door column between the rail and the content — §13.2's
   * answer for a door set too long for one tab row (the Library's eight, the
   * Settings screen's thirteen).
   *
   * It is chrome, not content: it is anchored on every sibling state, so the
   * page publishes it once here rather than re-rendering it inside each
   * section. A screen with a SubRail usually has no pane on its index state —
   * the Library home has no selectable row to extend.
   */
  subrail?: ReactNode;
  /** Extra controls between the bell and the primary. Used sparingly. */
  extra?: ReactNode;
  /** The canvas's search clamp — the nurse boards draw 260/170/260. */
  searchSize?: 'default' | 'narrow';
  /** Docked beside the content. Law 4 — an extension of the selection. */
  pane?: ReactNode;
  /**
   * THE PHONE'S HALF OF LAW 4. A phone cannot dock anything beside a 390px
   * column, so a pane that extends a selection becomes the selection's OWN
   * screen there — the way every phone board draws it ("Queue ▸ Karim B.").
   *
   * The shell cannot tell a selection from an empty pane by looking (most
   * pages dock an EmptyPane when nothing is picked), so the page says so:
   * `paneOpen` is true exactly while something is selected, and
   * `onPaneClose` clears that selection — it is the screen's back. A page
   * that sets neither keeps its pane desk-only, which is the old behaviour.
   */
  paneOpen?: boolean;
  onPaneClose?: () => void;
  /** The ☰ on the phone pane screen — a screen's own menu, so it is never a dead button. */
  paneMenu?: (close: () => void) => ReactNode;
  /** The phone pane screen's title ("Karim B."); the page title becomes its crumb. */
  paneTitle?: ReactNode;
  /** The strip's crumb on the pane screen when it is longer than the list's title ("Settings ▸ Flows"). */
  panePhoneCrumb?: ReactNode;
  /**
   * A COMPANION pane — always present because it is part of the task, not an
   * extension of a pick (a new bill's total and Collect, an export's package).
   * On a phone it FOLLOWS the content instead of covering it. Only for pages
   * on the shell fallback; a page with its own phone board places it itself.
   */
  paneInline?: boolean;
  /**
   * This page draws KIT markup and wants the kit's own content column, not the
   * legacy `max-w-7xl` page container. An un-ported page needs that container
   * (it is what gives ~70 pages a single width); a ported one already has
   * `.dcontent`, and nesting the two double-pads every design screen.
   */
  raw?: boolean;
  /**
   * THE PHONE LAYOUT, when the page draws one. Below the desk breakpoint the
   * shell renders this alone — no rail, no top bar, no docked pane — because a
   * phone screen brings its own top strip and ☰ (README §5: nav, sheet and
   * density are deliberate phone/desk divergences, not breakpoints of one
   * layout). Pages that publish no phone layout keep the desk chrome as before.
   */
  phone?: ReactNode;
  /**
   * THE LANE'S ☰ ON A PAGE THAT HAS NOT DRAWN A PHONE BOARD. Without it the
   * fallback builds the menu out of the rail's own doors, which is correct but
   * generic — it cannot carry the sub-lines, the "Home" chip or the line about
   * what deliberately does NOT open from there, and those are the parts of a
   * lane's menu that teach the model.
   *
   * One line on a page's chrome adopts the lane menu while the page itself
   * stays on the fallback. Ignored when `phone` is set: that board draws its
   * own PhoneScreen and passes the menu to it directly.
   */
  phoneMenu?: (close: () => void) => ReactNode;
  /**
   * THE FALLBACK'S TOP STRIP, AS THE PHONE BOARDS DRAW IT — "‹ Reports ▸
   * Revenue · Aug". The desk bar's title is a heading; a phone strip is a
   * place, so a page on the fallback can name where it sits (`phoneCrumb`),
   * give itself a shorter name there (`phoneTitle`), take a back
   * (`onPhoneBack`) and put one control at the strip's end (`phoneTrailing`,
   * which replaces the primary/secondary chips there). All ignored when
   * `phone` is set — that board draws its own strip.
   */
  phoneCrumb?: ReactNode;
  phoneTitle?: ReactNode;
  onPhoneBack?: () => void;
  phoneTrailing?: ReactNode;
  /**
   * The strip's caption on a phone. A page that names its place with
   * `phoneCrumb` drops the desk sub-line there (it usually repeats the crumb
   * or the filters already on screen); this puts one back.
   */
  phoneSub?: ReactNode;
  /**
   * False hides the search field on a phone. Most phone boards that are a
   * report or a settings page draw no search; the desk bar keeps it.
   */
  phoneSearch?: boolean;
  /**
   * What the bell opens. Default: the notifications page. A role whose canvas
   * draws the bell as its own overlay (scoped to what that role can act on)
   * passes it here.
   */
  onBell?: () => void;
}

interface ShellChromeStore {
  chrome: ShellChrome;
  set: (next: ShellChrome) => void;
}

const Ctx = createContext<ShellChromeStore | null>(null);

export function ShellChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<ShellChrome>({});
  const value = useMemo(() => ({ chrome, set: setChrome }), [chrome]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The layout side: what to render right now. */
/**
 * Below the desk breakpoint. It MOVED INTO THE KIT, because the kit itself is
 * now the biggest caller — a DataTable draws cards instead of columns down
 * here. Re-exported from its old home so the thirty-odd pages that import it
 * from the shell keep working.
 */
export { useNarrowViewport } from '@/components/kit/useNarrow';

export function useShellChromeValue(): ShellChrome {
  return useContext(Ctx)?.chrome ?? {};
}

/**
 * The page side: declare this screen's chrome.
 *
 * Cleared on unmount, so a page that leaves cannot leave its title or its
 * primary action behind on the next one — a stale "Continue visit · draft"
 * sitting above the settings page is exactly the §8 dead control the design
 * spends a section on.
 */
export function useShellChrome(chrome: ShellChrome, deps: unknown[] = []) {
  const store = useContext(Ctx);
  const latest = useRef(chrome);
  latest.current = chrome;

  useEffect(() => {
    store?.set(latest.current);
    return () => store?.set({});
    // The caller owns the dependency list; `chrome` itself is rebuilt on every
    // render, so it is read through a ref rather than being a dependency.
    //
    // PASS PRIMITIVES. A dep that is rebuilt each render — an array from
    // `.filter()`, an object literal — makes this effect re-run, which sets
    // state, which re-renders, which rebuilds the dep. React stops it at the
    // update-depth limit and logs 180 errors; nothing renders wrong, so it is
    // easy to miss. Lengths and ids, not the collections themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
